import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useTransactionalLog } from 'services/transactionalLogs'
import {
  buildCallPayload,
  CallEventKey,
  callEventName,
} from 'services/transactionalLogs/builders/callLogBuilders'
import DeviceSettingsModal from './DeviceSettingsModal'
import NetworkQualityPill from './NetworkQualityPill'
import VideoCallMeetControls from './VideoCallMeetControls'
import VideoCallMeetLayout from './VideoCallMeetLayout'
import VirtualBackgroundPanel from './VirtualBackgroundPanel'
import { DevicesProvider } from './contexts/DevicesContext'
import { NetworkQualityProvider } from './contexts/NetworkQualityContext'
import {
  useVirtualBackground,
  VirtualBackgroundProvider,
} from './contexts/VirtualBackgroundContext'
import { useAgoraRoom } from './hooks/useAgoraRoom'
import { useLocalTracks } from './hooks/useLocalTracks'
import {
  Body,
  ErrorMessage,
  Header,
  RemoteStatusNotice,
  Wrapper,
} from './styles'
import { VIDEO_CALL_MEET_TEST_IDS } from './testIds'
import { RemoteUserState } from './types'
import type { LeaveResult } from './rtc/agoraEngine'
import { VideoRoomSession } from 'types/videoRoomSesssion'
import { useUser } from 'context/user/user.context'
import api from 'configs/api'

/**
 * Tempo máximo que "Encerrar" espera o client.leave antes de fechar a tela.
 * Câmera e microfone já são fechados de forma síncrona no início do leave.
 * Fica acima do limite interno do SDK (o LEAVE do gateway desiste em 3s no
 * agora-rtc-sdk-ng 4.24.4) para CALL_LEAVE_TIMEOUT só aparecer quando o
 * leave de fato travou.
 */
export const END_CALL_LEAVE_TIMEOUT_MS = 5000

const MAX_LOG_ERROR_MESSAGE_LENGTH = 200

/**
 * Texto de erro que vai para o log transacional: o código do erro quando
 * existe (AgoraRTCError), senão a mensagem truncada. Rejeições que não são
 * Error não são serializadas, para não vazar conteúdo arbitrário.
 */
export function toLogErrorMessage(error: unknown): string {
  if (!(error instanceof Error)) return 'non-error rejection'
  const code = (error as { code?: unknown }).code
  const text = typeof code === 'string' && code ? code : error.message
  return (text || error.name).slice(0, MAX_LOG_ERROR_MESSAGE_LENGTH)
}

const LEAVE_TIMEOUT_ERROR = 'LEAVE_TIMEOUT'

function withLeaveTimeout(
  leave: Promise<LeaveResult>,
  ms: number
): Promise<LeaveResult> {
  let timer: ReturnType<typeof setTimeout> | undefined
  const timeout = new Promise<LeaveResult>((resolve) => {
    timer = setTimeout(
      () => resolve({ ok: false, error: new Error(LEAVE_TIMEOUT_ERROR) }),
      ms
    )
  })
  return Promise.race([leave, timeout]).finally(() => clearTimeout(timer))
}

interface VideoCallMeetProps {
  onCloseRoom: () => void
  token: string
  schedulingId: string
  roomSession: VideoRoomSession
  /**
   * When the doctor ends the call from the in-call controls (not on unmount).
   * Use to open CSAT / quality survey for this scheduling.
   */
  onDoctorEndedCall?: (schedulingId: string) => void
}

interface VideoCallMeetInnerProps extends VideoCallMeetProps {
  room: ReturnType<typeof useAgoraRoom>
}

function VideoCallMeetInner({
  onCloseRoom,
  onDoctorEndedCall,
  token,
  schedulingId,
  room,
  roomSession,
}: VideoCallMeetInnerProps) {
  const { userData } = useUser()
  const { sendLog } = useTransactionalLog()
  const {
    engine,
    remoteUsers,
    networkQuality,
    networkMessage,
    connectionStatus,
    joinError,
    tokenDidExpire,
    tokenWillExpire,
    joinRoom,
    leaveRoom,
  } = room
  const { mode } = useVirtualBackground()
  const { isAudioMuted, isVideoEnabled, toggleAudio, toggleVideo } =
    useLocalTracks(engine)
  const [isFullscreen, setIsFullscreen] = useState(false)
  const [showDevices, setShowDevices] = useState(false)
  const [showBackground, setShowBackground] = useState(false)
  const [remoteNotice, setRemoteNotice] = useState<string | null>(null)
  const wrapperRef = useRef<HTMLDivElement | null>(null)
  const correlationIdRef = useRef(`corr_${Date.now()}_${schedulingId}`)
  // Encerramento pelo botão: impede duplo clique e evita que o cleanup do
  // unmount repita leave, log e presence já feitos pelo handleEndCall.
  const endCallRef = useRef({ ending: false, presenceSent: false })
  const previousConnectionRef = useRef<string | null>(null)
  const remoteNoticeTimeoutRef = useRef<number | null>(null)
  const previousRemoteStatesRef = useRef<Record<string, RemoteUserState>>({})

  const context = useMemo(
    () => ({
      appointmentId: schedulingId || '',
      correlationId: correlationIdRef.current,
    }),
    [schedulingId]
  )

  const localTracks = engine.getLocalTracks()

  const emitLog = (event: CallEventKey, extra?: Record<string, unknown>) => {
    sendLog(
      callEventName(event),
      buildCallPayload(event, context, {
        ...extra,
        connectionStatus,
        networkMessage,
        txQuality: networkQuality.txQuality,
        rxQuality: networkQuality.rxQuality,
        overallQuality: networkQuality.overallQuality,
      })
    )
  }

  // O engine nunca rejeita o leave; o catch nos consumidores é só defesa.
  const logLeaveFailure = (error: unknown) => {
    const timedOut =
      error instanceof Error && error.message === LEAVE_TIMEOUT_ERROR
    emitLog('leave.failed', {
      errorCode: timedOut ? 'CALL_LEAVE_TIMEOUT' : 'CALL_LEAVE_FAILED',
      errorMessage: toLogErrorMessage(error),
    })
  }

  const sendPresence = async (
    type: 'join' | 'leave',
    options?: { connectionId?: string; providerCallUid?: number }
  ) => {
    const appointmentId = roomSession.channelName
    if (!appointmentId) return
    const userId = userData?.id
    if (!userId) {
      console.warn('[VideoCall] userId não disponível para presence')
      return
    }

    const providerCallUid = String(options?.providerCallUid ?? roomSession.uid)

    try {
      await api.post(
        `/video/v3/room/appointment/${appointmentId}/presence/${type}`,
        {
          channelName: roomSession.channelName,
          userId,
          providerCallUid,
          platform: 'professional',
          role: 'publisher',
          connectionId: correlationIdRef.current,
          metadata: {
            source: 'professional',
          },
        }
      )
    } catch (error) {
      console.warn(`[VideoCall] falha ao enviar presence.${type}:`, error)
    }
  }

  useEffect(() => {
    if (!token || !schedulingId) return

    let active = true
    // Um objeto por sessão (token/schedulingId): um encerramento pendente da
    // sessão anterior altera só o próprio objeto, nunca o da sessão nova.
    const session = { ending: false, presenceSent: false }
    endCallRef.current = session
    emitLog('join.requested')
    joinRoom(
      roomSession.channelName,
      roomSession.token,
      roomSession.uid,
      schedulingId
    )
      .then((joined) => {
        if (!active) return
        // Join cancelado por leave (troca de agendamento/remount): sem presence.
        if (!joined) {
          emitLog('join.cancelled')
          return
        }
        sendPresence('join')
        emitLog('join.succeeded')
      })
      .catch((error) => {
        if (!active) return
        emitLog('join.failed', {
          errorCode: 'CALL_JOIN_FAILED',
          errorMessage: toLogErrorMessage(error),
          httpStatus: 500,
        })
      })

    return () => {
      active = false
      if (!session.ending) {
        void leaveRoom()
          .then((result) => {
            if (result.ok) emitLog('leave')
            else logLeaveFailure(result.error)
          })
          .catch(logLeaveFailure)
      }
      if (!session.presenceSent) {
        session.presenceSent = true
        sendPresence('leave')
      }
    }
  }, [token, schedulingId])

  useEffect(() => {
    if (!tokenWillExpire) return
    emitLog('token.expired')
  }, [tokenWillExpire])

  useEffect(() => {
    if (!tokenDidExpire) return
    emitLog('token.expired')
  }, [tokenDidExpire])

  useEffect(() => {
    if (!previousConnectionRef.current) {
      previousConnectionRef.current = connectionStatus
      return
    }

    const previous = previousConnectionRef.current
    if (previous === connectionStatus) return

    const degradedSet = ['poor', 'critical', 'disconnected']
    const wasDegraded = degradedSet.includes(previous)
    const isDegraded = degradedSet.includes(connectionStatus)
    if (!wasDegraded && isDegraded) {
      emitLog('connection.quality.degraded', {
        previousConnectionStatus: previous,
      })
    } else if (wasDegraded && !isDegraded) {
      emitLog('connection.quality.recovered', {
        previousConnectionStatus: previous,
      })
    }
    previousConnectionRef.current = connectionStatus
  }, [connectionStatus, networkMessage])

  const handleToggleAudio = () => {
    void toggleAudio().then((muted) => {
      emitLog('microphone.toggled', { muted })
    })
  }

  const handleToggleVideo = () => {
    void toggleVideo().then((enabled) => {
      emitLog('video.toggled', { enabled })
    })
  }

  // Validação para garantir que o elemento existe e que o documento é um Document, pois no desktop o document é um Window e no mobile é um Document
  const handleToggleFullscreen = () => {
    const element = wrapperRef.current
    if (!element) return

    const el = element as HTMLElement & {
      webkitRequestFullscreen?: () => Promise<void> | void
      mozRequestFullScreen?: () => Promise<void> | void
      msRequestFullscreen?: () => Promise<void> | void
    }
    const doc = document as Document & {
      webkitExitFullscreen?: () => Promise<void> | void
      msExitFullscreen?: () => Promise<void> | void
    }

    if (doc.fullscreenElement) {
      if (typeof doc.exitFullscreen === 'function') {
        void doc.exitFullscreen()
      } else if (typeof doc.webkitExitFullscreen === 'function') {
        void doc.webkitExitFullscreen()
      } else if (typeof doc.msExitFullscreen === 'function') {
        void doc.msExitFullscreen()
      }
      setIsFullscreen(false)
      emitLog('fullscreen.toggled', { fullscreen: false })
      return
    }

    if (isFullscreen) {
      setIsFullscreen(false)
      emitLog('fullscreen.toggled', { fullscreen: false })
      return
    }

    const hasRequestFullscreen =
      typeof el.requestFullscreen === 'function' ||
      typeof el.webkitRequestFullscreen === 'function' ||
      typeof el.mozRequestFullScreen === 'function' ||
      typeof el.msRequestFullscreen === 'function'

    if (!hasRequestFullscreen) {
      setIsFullscreen(true)
      emitLog('fullscreen.toggled', { fullscreen: true })
      return
    }

    if (typeof el.requestFullscreen === 'function') {
      void el.requestFullscreen()
    } else if (typeof el.webkitRequestFullscreen === 'function') {
      void el.webkitRequestFullscreen()
    } else if (typeof el.mozRequestFullScreen === 'function') {
      void el.mozRequestFullScreen()
    } else {
      void el.msRequestFullscreen!()
    }
    setIsFullscreen(true)
    emitLog('fullscreen.toggled', { fullscreen: true })
  }

  const handleEndCall = () => {
    const session = endCallRef.current
    if (session.ending) return
    session.ending = true
    // Mesmo se o leave falhar, a tela fecha: o profissional não pode ficar
    // preso na chamada.
    void withLeaveTimeout(leaveRoom(), END_CALL_LEAVE_TIMEOUT_MS)
      .then((result) => {
        if (result.ok) emitLog('leave')
        else logLeaveFailure(result.error)
      })
      .catch(logLeaveFailure)
      .finally(() => {
        if (schedulingId && onDoctorEndedCall) {
          // Callback do pai (avaliação/analytics) não pode impedir de fechar.
          try {
            onDoctorEndedCall(schedulingId)
          } catch (error) {
            console.warn('[VideoCall] onDoctorEndedCall falhou:', error)
          }
          if (!session.presenceSent) {
            session.presenceSent = true
            sendPresence('leave')
          }
        }
        onCloseRoom()
      })
  }

  useEffect(() => {
    emitLog('background.changed', { backgroundType: mode })
  }, [mode])

  useEffect(() => {
    const previousRemoteStates = previousRemoteStatesRef.current
    const hasPreviousSnapshot = Object.keys(previousRemoteStates).length > 0
    const nextSnapshot: Record<string, RemoteUserState> = {}

    remoteUsers.forEach((user) => {
      nextSnapshot[user.uid] = user
    })

    if (!hasPreviousSnapshot) {
      previousRemoteStatesRef.current = nextSnapshot
      return
    }

    let notice: string | null = null

    remoteUsers.forEach((user) => {
      const previous = previousRemoteStates[user.uid]
      if (!previous) return

      const label = 'Paciente'

      if (previous.hasAudio !== user.hasAudio) {
        notice = user.hasAudio
          ? `${label} ativou o microfone`
          : `${label} mutou o microfone`
      }

      if (previous.hasVideo !== user.hasVideo) {
        notice = user.hasVideo
          ? `${label} ligou a camera`
          : `${label} desligou a camera`
      }
    })

    previousRemoteStatesRef.current = nextSnapshot

    if (!notice) return

    if (remoteNoticeTimeoutRef.current) {
      window.clearTimeout(remoteNoticeTimeoutRef.current)
    }

    setRemoteNotice(notice)
    remoteNoticeTimeoutRef.current = window.setTimeout(() => {
      setRemoteNotice(null)
      remoteNoticeTimeoutRef.current = null
    }, 2600)
  }, [remoteUsers])

  useEffect(() => {
    return () => {
      if (remoteNoticeTimeoutRef.current) {
        window.clearTimeout(remoteNoticeTimeoutRef.current)
      }
    }
  }, [])

  return (
    <Wrapper
      $maximized={isFullscreen}
      ref={wrapperRef}
      data-testid={VIDEO_CALL_MEET_TEST_IDS.root}
    >
      <Header data-testid={VIDEO_CALL_MEET_TEST_IDS.header}>
        <NetworkQualityPill />
      </Header>
      {joinError && (
        <ErrorMessage data-testid={VIDEO_CALL_MEET_TEST_IDS.errorMessage}>
          {joinError}
        </ErrorMessage>
      )}
      <Body data-testid={VIDEO_CALL_MEET_TEST_IDS.body}>
        {remoteNotice && (
          <RemoteStatusNotice
            data-testid={VIDEO_CALL_MEET_TEST_IDS.remoteNotice}
          >
            {remoteNotice}
          </RemoteStatusNotice>
        )}
        <VideoCallMeetLayout
          localVideoTrack={localTracks.videoTrack}
          localAudioMuted={isAudioMuted}
          localVideoEnabled={isVideoEnabled}
          remoteUsers={remoteUsers}
          isFullscreen={isFullscreen}
        />
        <DeviceSettingsModal
          isOpen={showDevices}
          onClose={() => setShowDevices(false)}
        />
        <VirtualBackgroundPanel
          isOpen={showBackground}
          onClose={() => setShowBackground(false)}
        />
      </Body>
      <VideoCallMeetControls
        isAudioMuted={isAudioMuted}
        isVideoEnabled={isVideoEnabled}
        isFullscreen={isFullscreen}
        onToggleAudio={handleToggleAudio}
        onToggleVideo={handleToggleVideo}
        onToggleFullscreen={handleToggleFullscreen}
        onToggleDevices={() => setShowDevices((current) => !current)}
        onToggleBackground={() => setShowBackground((current) => !current)}
        onEndCall={handleEndCall}
      />
    </Wrapper>
  )
}

export default function VideoCallMeet(props: VideoCallMeetProps) {
  const room = useAgoraRoom()
  return (
    <NetworkQualityProvider
      networkQuality={room.networkQuality}
      connectionStatus={room.connectionStatus}
      networkMessage={room.networkMessage}
    >
      <DevicesProvider engine={room.engine}>
        <VirtualBackgroundProvider engine={room.engine}>
          <VideoCallMeetInner {...props} room={room} />
        </VirtualBackgroundProvider>
      </DevicesProvider>
    </NetworkQualityProvider>
  )
}
